import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { RootState } from '..'

const needsPhoneVerification = (msg: unknown) => {
    if (!msg) return false
    const text = typeof msg === 'string' ? msg.toLowerCase() : ''
    if (!text) return false
    return (
        text.includes('verify your phone') ||
        text.includes('phone not verified') ||
        text.includes('verify phone') ||
        text.includes('phone number not verified')
    )
}

const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://checker-api.podus.org/api/v1'

console.log('[API] Using baseUrl:', baseUrl)

const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
        // IMMEDIATE check - what's in localStorage RIGHT NOW?
        const immediateCheck = localStorage.getItem('access_token')
        console.log('[Auth] IMMEDIATE localStorage check:', immediateCheck ? `EXISTS (${immediateCheck.substring(0, 20)}...)` : 'NULL')

        // Get token from Redux state first, fallback to localStorage
        const reduxToken = (getState() as RootState).auth.token
        const storageToken = localStorage.getItem('access_token')
        const token = reduxToken || storageToken

        console.log('[Auth] prepareHeaders - Redux token:', reduxToken ? `exists (${reduxToken.substring(0, 20)}...)` : 'null', 'Storage token:', storageToken ? `exists (${storageToken?.substring(0, 20)}...)` : 'null')

        // If we have a token, include it in the headers
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
            console.log('[Auth] Authorization header set')
        } else {
            console.log('[Auth] No token available for request')
        }

        return headers
    },
})

// Custom base query with automatic token refresh
export const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions)

    // Check if we got a 401 unauthorized error
    if (result.error && result.error.status === 401) {
        const url = typeof args === 'string' ? args : args.url
        const rawMessage =
            (result.error.data as any)?.message ||
            (result.error.data as any)?.error ||
            (result.error as any)?.error ||
            ''

        console.log('[Auth] 401 error detected:', { url, rawMessage })

        // If backend says phone verification is required, send user to verification flow instead of logging them out
        if (needsPhoneVerification(rawMessage)) {
            console.log('[Auth] Phone verification required, redirecting to verify-account')
            const state = api.getState() as RootState
            const phone = state.auth.user?.phoneNumber || localStorage.getItem('pending_verification_phone') || ''
            if (phone) {
                localStorage.setItem('pending_verification_phone', phone)
            }
            if (!window.location.pathname.includes('/verify-account')) {
                window.location.href = '/verify-account'
            }
            return result
        }

        // Don't try to refresh token if we're on auth endpoints (login, register, refresh)
        // These endpoints should return errors directly to the caller
        const isAuthEndpoint =
            typeof url === 'string' &&
            (url.includes('/auth/login') ||
                url.includes('/auth/register') ||
                url.includes('/auth/refresh') ||
                url.includes('/auth/send-otp') ||
                url.includes('/auth/verify-otp'))

        if (isAuthEndpoint) {
            // For auth endpoints, just return the error without any redirect
            return result
        }

        // Try to get a new token
        const refreshToken = localStorage.getItem('refresh_token')

        if (refreshToken) {
            console.log('[Auth] Attempting token refresh')
            const refreshResult = await baseQuery(
                {
                    url: '/auth/refresh',
                    method: 'POST',
                    body: { refreshToken },
                },
                api,
                extraOptions
            )

            if (refreshResult.data) {
                console.log('[Auth] Token refresh successful')
                const { access_token, refresh_token } = refreshResult.data as {
                    access_token: string
                    refresh_token: string
                }

                // Store the new tokens
                localStorage.setItem('access_token', access_token)
                localStorage.setItem('refresh_token', refresh_token)

                // Update Redux state with new token
                api.dispatch({ type: 'auth/setToken', payload: access_token })

                // Retry the original query with new token
                result = await baseQuery(args, api, extraOptions)
            } else {
                // Refresh failed - clear auth state and redirect to login
                console.log('[Auth] Token refresh failed, logging out:', refreshResult.error)
                api.dispatch({ type: 'auth/logout' })
                localStorage.removeItem('access_token')
                localStorage.removeItem('refresh_token')

                // Only redirect if not already on login page
                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login'
                }
            }
        } else {
            // No refresh token - clear auth and redirect to login
            console.log('[Auth] No refresh token found, logging out')
            api.dispatch({ type: 'auth/logout' })

            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login'
            }
        }
    }

    // Unwrap the { success, data, timestamp } structure from API responses
    if (result.data && typeof result.data === 'object' && 'data' in result.data) {
        const wrappedResponse = result.data as { success: boolean; data: unknown; timestamp: string };
        return { ...result, data: wrappedResponse.data };
    }

    return result
}
